import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import api from '../services/api';

type Agency = {
  _id: string;
  slug: string;
  name: string;
  email: string;
  campaignBalanceCents?: number;
};

const AdminCampaignCredits = () => {
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [topupDollarsByAgency, setTopupDollarsByAgency] = useState<Record<string, string>>({});
  const [submittingAgencyId, setSubmittingAgencyId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAgency, setConfirmAgency] = useState<Agency | null>(null);
  const [confirmAmountCents, setConfirmAmountCents] = useState<number>(0);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/admin/agencies');
      setAgencies(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to fetch agencies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agencies;
    return agencies.filter((a) => {
      return (
        a.name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.slug?.toLowerCase().includes(q)
      );
    });
  }, [agencies, search]);

  const handleTopup = async (agency: Agency) => {
    const raw = (topupDollarsByAgency[agency._id] || '').trim();
    const amountDollars = Number(raw);
    if (!Number.isFinite(amountDollars) || amountDollars <= 0) {
      setError('Please enter a valid amount in dollars (> 0).');
      return;
    }
    const amountCents = Math.round(amountDollars * 100);
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      setError('Please enter a valid amount in dollars (> 0).');
      return;
    }

    setConfirmAgency(agency);
    setConfirmAmountCents(amountCents);
    setConfirmOpen(true);
  };

  const handleConfirmTopup = async () => {
    if (!confirmAgency) return;
    const agency = confirmAgency;
    const amountCents = confirmAmountCents;

    try {
      setSubmittingAgencyId(agency._id);
      setError(null);
      await api.post(`/agencies/${agency.slug}/campaign-credits/deposit`, { amountCents });
      setSuccess(`Deposited $${(amountCents / 100).toFixed(2)} to ${agency.name}`);
      await fetchAgencies();
      setTopupDollarsByAgency((prev) => ({ ...prev, [agency._id]: '' }));
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.response?.data?.message || e?.message || 'Failed to deposit credits');
    } finally {
      setSubmittingAgencyId(null);
      setConfirmOpen(false);
      setConfirmAgency(null);
      setConfirmAmountCents(0);
    }
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Campaign Credits (Manual Top-up)
        </Typography>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search agencies (name, email, slug)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <Button variant="outlined" onClick={fetchAgencies} disabled={loading}>
                {loading ? 'Loading…' : 'Refresh'}
              </Button>
            </Grid>
          </Grid>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Use this only for support scenarios (e.g., webhook missed). This directly credits the agency wallet.
          </Alert>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Agency</TableCell>
                <TableCell>Slug</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Manual deposit (AUD $)</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((agency) => {
                const balanceCents = Number(agency.campaignBalanceCents || 0);
                const busy = submittingAgencyId === agency._id;
                return (
                  <TableRow key={agency._id}>
                    <TableCell>{agency.name}</TableCell>
                    <TableCell>{agency.slug}</TableCell>
                    <TableCell>{agency.email}</TableCell>
                    <TableCell align="right">${(balanceCents / 100).toFixed(2)}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                        value={topupDollarsByAgency[agency._id] ?? ''}
                        onChange={(e) =>
                          setTopupDollarsByAgency((prev) => ({ ...prev, [agency._id]: e.target.value }))
                        }
                        placeholder="e.g. 50"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        onClick={() => handleTopup(agency)}
                        disabled={busy}
                      >
                        {busy ? 'Depositing…' : 'Deposit'}
                      </Button>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>

        <Snackbar open={!!error} autoHideDuration={8000} onClose={() => setError(null)}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
          <Alert severity="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </Snackbar>

        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirm manual deposit</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You are about to deposit:
            </Typography>
            <Typography variant="h6">
              ${ (confirmAmountCents / 100).toFixed(2) }
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Agency: <strong>{confirmAgency?.name}</strong> ({confirmAgency?.slug})
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action directly credits the agency wallet and should be used only for support scenarios.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => setConfirmOpen(false)}
              disabled={!!submittingAgencyId}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirmTopup}
              disabled={!!submittingAgencyId}
            >
              Confirm deposit
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminCampaignCredits;


